import React from 'react';
import {
  Dialog,
  DialogTitle,
  List,
  ListItemIcon,
  ListItemText,
  Radio,
  Button,
  ListItemButton,
  Typography,
  Box,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useMarkets } from '../../api/product';
import type { Market } from '../../api/product';

interface CountrySelectionDialogProps {
  open: boolean;
  onClose: () => void;
  selectedCountry: {
    code: string;
    name: string;
    marketId: number;
    marketName: string;
  } | null;
  onSelectCountry: (countryCode: string, countryName: string, marketId: number, marketName: string) => void;
  selectedMarketId?: number; // Add selected market ID prop
}

const CountrySelectionDialog: React.FC<CountrySelectionDialogProps> = ({
  open,
  onClose,
  selectedCountry,
  onSelectCountry,
  selectedMarketId,
}) => {
  const { marketsData } = useMarkets();

  // Filter countries to only show countries from the selected market
  const availableCountries = React.useMemo(() => {
    if (!marketsData || !selectedMarketId) return [];
    
    const selectedMarket = marketsData.find((market: Market) => market.id === selectedMarketId);
    if (!selectedMarket) return [];
    
    return selectedMarket.countriesWithNames.map(country => ({
      code: country.code,
      name: country.name,
      marketId: selectedMarket.id,
      marketName: selectedMarket.name,
    })).sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by country name
  }, [marketsData, selectedMarketId]);

  const handleSelectCountry = (countryCode: string, countryName: string, marketId: number, marketName: string) => {
    onSelectCountry(countryCode, countryName, marketId, marketName);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        style={{
          fontWeight: 700,
          fontSize: 20,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        Select your shipping country ({availableCountries.length > 0 ? availableCountries[0].marketName : 'Market'}):
      </DialogTitle>
      <List>
        {availableCountries.map((country) => {
          const isSelected = selectedCountry && selectedCountry.code === country.code;
          return (
            <ListItemButton
              key={`${country.code}-${country.marketId}`}
              onClick={() => handleSelectCountry(country.code, country.name, country.marketId, country.marketName)}
              style={{
                background: isSelected ? '#b48b6a' : '#fff',
                color: isSelected ? '#fff' : '#131518',
                marginBottom: 8,
                alignItems: 'flex-start',
                padding: '16px 24px',
                display: 'flex',
              }}
            >
              <ListItemIcon
                style={{
                  minWidth: 36,
                  color: isSelected ? '#fff' : '#b48b6a',
                }}
              >
                <PublicIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box>
                    <Typography
                      style={{ 
                        fontWeight: 700, 
                        fontSize: 16,
                        color: isSelected ? '#fff' : '#131518'
                      }}
                    >
                      {country.name}
                    </Typography>
                    <Typography
                      style={{ 
                        fontWeight: 500, 
                        fontSize: 12,
                        color: isSelected ? '#fff' : '#666',
                        marginTop: 2
                      }}
                    >
                      {country.marketName} Market
                    </Typography>
                  </Box>
                }
              />
              {isSelected && <Radio checked style={{ color: '#fff' }} />}
            </ListItemButton>
          );
        })}
      </List>
      <Button onClick={onClose} style={{ margin: 16 }}>
        Close
      </Button>
    </Dialog>
  );
};

export default CountrySelectionDialog; 